import { shallowEqual } from 'fast-equals'

/**
 * Returns the number of copies for a given item at a given index.
 *
 * @example
 * ```ts
 * const entities = [
 *     { guid: '123', title: 'foo' },
 *     { guid: '456', title: 'bar' },
 * ]
 * const entity = { guid: '123', title: 'foo' }
 * nthCopy(entities, entity, (a, b) => a.title === b.title) // 1
 * ```
 *
 * @param entities - The list of entities to search through.
 * @param entity - The entity to search for.
 * @param match - The function to use to match the entities.
 * @returns The number of times the entity appears in the list.
 */
export default function nthCopy<T extends object = any>(
    entities: T[],
    entity: T,
    match: (a: T, b: T) => boolean,
): number {
    let numberOfCopies = 0

    for (const e of entities) {
        if (match(e, entity)) {
            numberOfCopies++
        }
        if (shallowEqual(e, entity)) {
            break
        }
    }

    return numberOfCopies
}
